import { useRef, useState } from "react";
import { ImagePlus, Plus, Trash2, X } from "lucide-react";
import { api, apiError, imgUrl } from "../../lib/api";
import { toast } from "sonner";
import { btnGhost, inputCls, labelCls } from "./shared";

const emptyOption = () => ({ label: "", price: "", stock: "", images: [], is_default: false, is_active: true });

// Structured variant groups editor (Colour / Size / any custom option).
// Each option can carry its own price, stock, images, default flag and on/off switch.
export default function VariantEditor({ groups, onChange }) {
  const [customName, setCustomName] = useState("");
  const imgRef = useRef(null);
  const [imgTarget, setImgTarget] = useState(null); // {gi, oi}
  const [uploading, setUploading] = useState(false);

  const update = (gi, patch) => onChange(groups.map((g, i) => (i === gi ? { ...g, ...patch } : g)));
  const updateOption = (gi, oi, patch) =>
    update(gi, { options: groups[gi].options.map((o, i) => (i === oi ? { ...o, ...patch } : o)) });

  const addGroup = (name) => {
    const n = (name || customName).trim();
    if (!n) { toast.error("Enter a variant name, e.g. Colour or Size"); return; }
    onChange([...groups, { name: n, options: [emptyOption()] }]);
    setCustomName("");
  };

  const addOptionImage = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length || !imgTarget) return;
    setUploading(true);
    const urls = [];
    for (const f of files) {
      const fd = new FormData();
      fd.append("file", f);
      try {
        const { data } = await api.post("/admin/upload-image", fd);
        urls.push(data.url);
      } catch (err) { toast.error(apiError(err)); }
    }
    if (urls.length) updateOption(imgTarget.gi, imgTarget.oi, { images: [...groups[imgTarget.gi].options[imgTarget.oi].images, ...urls] });
    setUploading(false);
    setImgTarget(null);
  };

  return (
    <div className="sm:col-span-2" data-testid="variant-editor">
      <p className={labelCls}>Product variants — colours, sizes or any custom option (kept inside this one product)</p>
      <div className="mt-2 space-y-4">
        {groups.map((g, gi) => (
          <div key={gi} className="rounded-2xl border border-input bg-white/60 p-4" data-testid={`variant-group-${gi}`}>
            <div className="flex flex-wrap items-center gap-3">
              <input className={`${inputCls} mt-0 max-w-[180px]`} data-testid={`variant-group-name-${gi}`} value={g.name}
                onChange={(e) => update(gi, { name: e.target.value })} placeholder="Colour / Size / Design" />
              <span className="text-[10px] uppercase tracking-widest text-muted-foreground">{g.options.length} option(s)</span>
              <button type="button" data-testid={`variant-group-delete-${gi}`} aria-label="Delete group"
                onClick={() => { if (window.confirm(`Delete the whole "${g.name}" variant group?`)) onChange(groups.filter((_, i) => i !== gi)); }}
                className="ml-auto rounded-full p-1.5 text-destructive hover:bg-secondary"><Trash2 size={14} /></button>
            </div>
            <div className="mt-3 space-y-3">
              {g.options.map((o, oi) => (
                <div key={oi} className="rounded-xl bg-secondary/40 p-3" data-testid={`variant-option-${gi}-${oi}`}>
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                    <input className={`${inputCls} mt-0`} data-testid={`variant-label-${gi}-${oi}`} value={o.label} placeholder="e.g. Black / M"
                      onChange={(e) => updateOption(gi, oi, { label: e.target.value })} />
                    <input className={`${inputCls} mt-0`} data-testid={`variant-price-${gi}-${oi}`} type="number" min="0" value={o.price} placeholder="Price ₹ (optional)"
                      onChange={(e) => updateOption(gi, oi, { price: e.target.value })} />
                    <input className={`${inputCls} mt-0`} data-testid={`variant-stock-${gi}-${oi}`} type="number" min="0" value={o.stock} placeholder="Stock (optional)"
                      onChange={(e) => updateOption(gi, oi, { stock: e.target.value })} />
                    <div className="flex items-center gap-3">
                      <label className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest">
                        <input type="radio" name={`default-${gi}`} data-testid={`variant-default-${gi}-${oi}`} checked={!!o.is_default}
                          onChange={() => update(gi, { options: g.options.map((x, i) => ({ ...x, is_default: i === oi })) })} className="accent-primary" /> Default
                      </label>
                      <label className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest">
                        <input type="checkbox" data-testid={`variant-active-${gi}-${oi}`} checked={!!o.is_active}
                          onChange={(e) => updateOption(gi, oi, { is_active: e.target.checked })} className="accent-primary" /> On
                      </label>
                      <button type="button" data-testid={`variant-option-delete-${gi}-${oi}`} aria-label="Delete option"
                        onClick={() => update(gi, { options: g.options.filter((_, i) => i !== oi) })}
                        className="ml-auto rounded-full p-1.5 text-destructive hover:bg-secondary"><X size={13} /></button>
                    </div>
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    {(o.images || []).map((u, ii) => (
                      <span key={u} className="relative">
                        <img src={imgUrl(u)} alt="" className="h-12 w-10 rounded-lg object-cover" />
                        <button type="button" data-testid={`variant-image-remove-${gi}-${oi}-${ii}`} aria-label="Remove image"
                          onClick={() => updateOption(gi, oi, { images: o.images.filter((_, j) => j !== ii) })}
                          className="absolute -right-1 -top-1 rounded-full bg-destructive p-0.5 text-white"><X size={8} /></button>
                      </span>
                    ))}
                    <button type="button" data-testid={`variant-image-add-${gi}-${oi}`} disabled={uploading}
                      onClick={() => { setImgTarget({ gi, oi }); imgRef.current?.click(); }}
                      className="flex h-12 w-10 items-center justify-center rounded-lg border border-dashed border-primary/50 text-primary hover:bg-secondary/50">
                      <ImagePlus size={14} />
                    </button>
                    <span className="text-[10px] font-light text-muted-foreground">Colour photos — shown when a customer picks this option</span>
                  </div>
                </div>
              ))}
            </div>
            <button type="button" data-testid={`variant-add-option-${gi}`} onClick={() => update(gi, { options: [...g.options, emptyOption()] })}
              className={`${btnGhost} mt-3 !px-4 !py-2`}><Plus size={13} /> Add option</button>
          </div>
        ))}
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button type="button" data-testid="variant-add-colour" onClick={() => addGroup("Colour")} className={btnGhost}><Plus size={13} /> Colour</button>
        <button type="button" data-testid="variant-add-size" onClick={() => addGroup("Size")} className={btnGhost}><Plus size={13} /> Size</button>
        <input className={`${inputCls} mt-0 max-w-[160px]`} data-testid="variant-custom-name" value={customName}
          onChange={(e) => setCustomName(e.target.value)} placeholder="Custom (e.g. Material)" />
        <button type="button" data-testid="variant-add-custom" onClick={() => addGroup()} className={btnGhost}><Plus size={13} /> Custom group</button>
        <input ref={imgRef} type="file" accept=".jpg,.jpeg,.png,.webp,image/*" multiple className="hidden" data-testid="variant-image-input" onChange={addOptionImage} />
      </div>
      <p className="mt-2 text-[11px] font-light text-muted-foreground">Customers must pick one option from each group before adding to bag. Empty price/stock = uses the main product price and stock.</p>
    </div>
  );
}
